// ✅ 개선된 NewPost.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import TuiEditor from '../components/TuiEditor';
import './NewPost.css';

function slugify(text) {
  return text
    .toString()
    .normalize('NFC')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()"'’‘”“〈〉<>\[\]\|·…\u200B]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

function NewPost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDevBlog = searchParams.get('dev') === '1';
  const editorRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    summary: '',
    thumbnail: '',
    content: '',
    category: isDevBlog ? 'tech' : '',
    tags: isDevBlog ? 'dev-blog' : '',
  });
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fileName = `thumbnail_${Date.now()}_${file.name}`;
    const { error } = await supabase.storage.from('thumbnails').upload(fileName, file);
    if (!error) {
      const { data } = supabase.storage.from('thumbnails').getPublicUrl(fileName);
      const shortUrl = data.publicUrl.split('?')[0];
      setForm((prev) => ({ ...prev, thumbnail: shortUrl }));
    } else {
      setShowModal('error');
    }
    setUploading(false);
  };

  const handleThumbnailRemove = () => {
    setForm((prev) => ({ ...prev, thumbnail: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const slug = slugify(form.title);
    const content = editorRef.current?.getInstance().getMarkdown();

    if (!slug || slug.length < 2 || !content) {
      alert('slug 또는 본문 내용이 유효하지 않습니다.');
      return;
    }

    let tags = form.tags.split(',').map((tag) => tag.trim()).filter(Boolean);
    if (isDevBlog && !tags.includes('dev-blog')) tags = ['dev-blog', ...tags];
    const post = {
      ...form,
      content,
      slug,
      tags,
      author: user?.email,
    };

    const { error } = await supabase.from('blog_posts').insert([post]);

    if (error) {
      console.error('Insert Error:', error.message);
      setShowModal('error');
    } else {
      setShowModal('success');
      localStorage.removeItem('autosave_post');
      setTimeout(() => navigate(isDevBlog ? '/dev-blog' : '/blog'), 1000);
    }
  };

  const handleCancel = () => {
    localStorage.removeItem('autosave_post');
    setShowModal('cancel');
  };

  useEffect(() => {
    const saved = localStorage.getItem('autosave_post');
    if (saved) setForm(JSON.parse(saved));
    else if (isDevBlog) setForm((prev) => ({ ...prev, tags: 'dev-blog', category: prev.category || 'tech' }));
  }, []);

  if (!user || user.email !== 'calicorone@gmail.com') {
    return <p style={{ padding: '2rem' }}>접근 권한이 없습니다.</p>;
  }

  return (
    <div className="new-post">
      <form onSubmit={handleSubmit} className="new-post-form">
        <input
          type="text"
          name="title"
          placeholder="제목을 입력하세요"
          value={form.title}
          onChange={handleChange}
          required
        />
        <input
          type="text"
          name="summary"
          placeholder="요약을 입력하세요"
          value={form.summary}
          onChange={handleChange}
          required
        />
        <input
          type="text"
          name="tags"
          placeholder="#태그, 쉼표로 구분"
          value={form.tags}
          onChange={handleChange}
        />
        <select name="category" value={form.category} onChange={handleChange} required>
          <option value="">카테고리 선택</option>
          <option value="tech">기술</option>
          <option value="life">일상</option>
          <option value="book">책</option>
          <option value="memoir">기억</option>
          <option value="etc">기타</option>
        </select>

        <div className="thumbnail-upload">
          <label>썸네일 이미지 업로드</label>
          <input type="file" accept="image/*" onChange={handleThumbnailChange} />
          {uploading && <p>업로드 중...</p>}
          {form.thumbnail && (
            <>
              <img src={form.thumbnail} alt="썸네일 미리보기" className="thumbnail-preview" />
              <button type="button" className="remove-thumbnail" onClick={handleThumbnailRemove}>
                ✖ 썸네일 제거
              </button>
            </>
          )}
        </div>

        <TuiEditor
          ref={editorRef}
          height="500px"
          initialEditType="markdown"
          previewStyle="vertical"
          onChange={() => {
            const content = editorRef.current?.getInstance().getMarkdown();
            setForm((prev) => ({ ...prev, content }));
          }}
        />

        <div className="button-group">
          <button type="button" className="cancel-btn" onClick={handleCancel}>
            취소하기
          </button>
          <button type="submit" className="submit-btn">
            등록하기
          </button>
        </div>
      </form>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            {showModal === 'success' && <p>✅ 글이 등록되었습니다!</p>}
            {showModal === 'error' && <p>❌ 오류가 발생했습니다. 다시 시도해 주세요.</p>}
            {showModal === 'cancel' && (
              <>
                <p>정말 작성을 취소하시겠습니까?</p>
                <div className="modal-buttons">
                  <button onClick={() => navigate(isDevBlog ? '/dev-blog' : '/blog')}>네</button>
                  <button onClick={() => setShowModal(null)}>아니요</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NewPost;
